import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// ========================================
// ASYNC THUNKS
// ========================================

// Fetch todas las ramas
export const fetchRamas = createAsyncThunk(
  'ramas/fetchRamas',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/ramas', { params })
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar ramas'
      )
    }
  }
)

// Fetch rama específica
export const fetchRamaById = createAsyncThunk(
  'ramas/fetchRamaById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ramas/${id}`)
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar rama'
      )
    }
  }
)

// Crear nueva rama
export const createRama = createAsyncThunk(
  'ramas/createRama',
  async (ramaData, { rejectWithValue }) => {
    try {
      const response = await api.post('/ramas', ramaData)
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear rama'
      )
    }
  }
)

// Actualizar rama
export const updateRama = createAsyncThunk(
  'ramas/updateRama',
  async ({ id, ...ramaData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/ramas/${id}`, ramaData)
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar rama'
      )
    }
  }
)

// Eliminar rama
export const deleteRama = createAsyncThunk(
  'ramas/deleteRama',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/ramas/${id}`)
      return id
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar rama'
      )
    }
  }
)

// Reordenar ramas
export const reordenarRamas = createAsyncThunk(
  'ramas/reordenarRamas',
  async (ordenes, { rejectWithValue }) => {
    try {
      const response = await api.put('/ramas/reordenar/batch', { ordenes })
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al reordenar ramas'
      )
    }
  }
)

// ========================================
// SLICE
// ========================================

const ramasSlice = createSlice({
  name: 'ramas',
  initialState: {
    ramas: [],
    ramaActual: null,
    loading: 'idle',
    error: null,
    successMessage: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null
    },
    clearRamaActual: (state) => {
      state.ramaActual = null
    },
    setRamasOrder: (state, action) => {
      // Actualizar orden localmente para UX instantánea
      state.ramas = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch ramas
      .addCase(fetchRamas.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(fetchRamas.fulfilled, (state, action) => {
        state.loading = 'fulfilled'
        state.ramas = action.payload.data || []
      })
      .addCase(fetchRamas.rejected, (state, action) => {
        state.loading = 'rejected'
        state.error = action.payload
      })

      // Fetch rama by ID
      .addCase(fetchRamaById.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(fetchRamaById.fulfilled, (state, action) => {
        state.loading = 'fulfilled'
        state.ramaActual = action.payload.data
      })
      .addCase(fetchRamaById.rejected, (state, action) => {
        state.loading = 'rejected'
        state.error = action.payload
      })

      // Create rama
      .addCase(createRama.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(createRama.fulfilled, (state, action) => {
        state.loading = 'fulfilled'
        state.ramas.push(action.payload.data)
        state.successMessage = action.payload.message
      })
      .addCase(createRama.rejected, (state, action) => {
        state.loading = 'rejected'
        state.error = action.payload
      })

      // Update rama
      .addCase(updateRama.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(updateRama.fulfilled, (state, action) => {
        state.loading = 'fulfilled'
        const index = state.ramas.findIndex(r => r.id === action.payload.data.id)
        if (index !== -1) {
          state.ramas[index] = action.payload.data
        }
        state.successMessage = action.payload.message
      })
      .addCase(updateRama.rejected, (state, action) => {
        state.loading = 'rejected'
        state.error = action.payload
      })

      // Delete rama
      .addCase(deleteRama.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(deleteRama.fulfilled, (state, action) => {
        state.loading = 'fulfilled'
        state.ramas = state.ramas.filter(r => r.id !== action.payload)
        state.successMessage = 'Rama eliminada exitosamente'
      })
      .addCase(deleteRama.rejected, (state, action) => {
        state.loading = 'rejected'
        state.error = action.payload
      })

      // Reordenar ramas
      .addCase(reordenarRamas.fulfilled, (state, action) => {
        state.successMessage = action.payload.message
      })
  }
})

export const { clearError, clearSuccessMessage, clearRamaActual, setRamasOrder } = ramasSlice.actions
export default ramasSlice.reducer
